"use client";

import { useRef, FC, useCallback, useState } from "react";
import cx from "classnames";
/* eslint-disable import/no-unresolved */
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import styles from "./styles.module.scss";
import "../../../styles/swiper.scss";
import CarCardGrid from "../vehicle-card/car-card-grid";
import InventoryCard from "../inventory-card/inventory-card";
import { Vehicle } from "../../../types/vehicle";
import { ClientSiteData } from "../../../types/site";
import ArrowIcon from "../../icons/arrow-icon";
import { useIsMobile } from "../../../utils/useIsMobile";
import { PageContext } from "../../../types/ui";
import { Translations } from "../../../utils/translations";
import { getVehicleUrl } from "../../../utils/ui";
import { CardTab, VehicleConditionalCardData } from "../../../types/blocks";

export interface IVehicleContainer {
  title?: string;
  subtitle?: string;
  vehicles: Vehicle[];
  className?: string;
  siteData: ClientSiteData;
  pageContext: PageContext;
  inventory?: boolean;
  vehicleCardConfig: VehicleConditionalCardData;
  translations: Translations;
  showTabs?: boolean;
}

const VehicleContainer: FC<IVehicleContainer> = ({
  title,
  subtitle,
  vehicles,
  className,
  siteData,
  pageContext,
  inventory,
  vehicleCardConfig,
  translations,
  showTabs,
}) => {
  const isMobile = useIsMobile(undefined, pageContext.isSmartphone);
  const isDesktop = useIsMobile(1600, pageContext.isSmartphone);
  const vehiclesSwiperRef = useRef(null) as any;
  const bulletClassName = `bullets${className}`;

  const vehicleClickHandler = useCallback((vehicle: Vehicle) => {
    window.open(getVehicleUrl(vehicle), "_self");
  }, []);

  const pagination = {
    clickable: true,
    el: `.${bulletClassName}`,
    bulletClass: `${styles.bullet}`,
    bulletActiveClass: `${styles.bulletActive}`,
    renderBullet: function (index: number, clsName: string) {
      return '<span class="' + clsName + '"></span>';
    },
  };

  const [isEnd, setIsEnd] = useState(false);
  const handleSlideChange = useCallback(() => {
    if (isEnd) {
      setIsEnd(false);
    }
  }, [isEnd]);

  return vehicles?.length > 0 ? (
    <section className={cx(styles.vehicles, className)}>
      <div className={styles.vehiclesContainer}>
        <div className={styles.titleContainer}>
          {title && (
            <h3 title={title} className={styles.title}>
              {title}
            </h3>
          )}
          {subtitle && (
            <span title={subtitle} className={styles.subtitle}>
              {subtitle}
            </span>
          )}
        </div>
        {vehicles?.length >= (isDesktop ? 3 : 4) && (
          <div className={styles.vehiclesArrows}>
            <button
              aria-label={"vehicles arrow"}
              name={"vehiclesArrow"}
              className={styles.vehiclesArrow}
              onClick={() => vehiclesSwiperRef.current.swiper.slidePrev()}
            >
              <ArrowIcon />
            </button>
            <button
              aria-label={"vehicles arrow"}
              name={"vehiclesArrow"}
              className={cx(styles.vehiclesArrow, styles.vehiclesArrowReversed)}
              onClick={() => vehiclesSwiperRef.current.swiper.slideNext()}
            >
              <ArrowIcon />
            </button>
          </div>
        )}
      </div>
      <div
        className={cx(styles.slider, {
          [styles.isEnd]: isEnd,
        })}
      >
        <Swiper
          onInit={(core: SwiperCore) => (vehiclesSwiperRef.current = core.el)}
          spaceBetween={isMobile ? 16 : 32}
          pagination={pagination}
          modules={[Pagination]}
          breakpoints={{
            320: {
              slidesPerView: "auto",
            },
            1280: {
              slidesPerView: 2.3,
            },
            1600: {
              slidesPerView: 3.5,
            },
          }}
          onSlideChange={handleSlideChange}
          onReachEnd={() => setIsEnd(true)}
        >
          {inventory
            ? vehicles.map((item) => (
                <SwiperSlide
                  key={item.id + Math.random()}
                  className={cx(styles.swiperSlide, {
                    [styles.swiperSlideInventory]: inventory,
                  })}
                >
                  <InventoryCard
                    className={styles.vehiclesCard}
                    vehicle={item}
                    siteData={siteData}
                    vehicleCardData={vehicleCardConfig}
                    pageContext={pageContext}
                    translations={translations}
                  />
                </SwiperSlide>
              ))
            : vehicles.map((item) => (
                <SwiperSlide key={item.id} className={styles.swiperSlide}>
                  <CarCardGrid
                    className={styles.vehiclesCard}
                    vehicle={item}
                    actionButtons={[]}
                    siteData={siteData}
                    pageContext={pageContext}
                    vehicleCardData={vehicleCardConfig}
                    defaultTab={CardTab.INFO}
                    translations={translations}
                    preventShowPrice={true}
                    onVehicleClick={vehicleClickHandler}
                    showClickToCall={false}
                    showTabs={showTabs}
                  />
                </SwiperSlide>
              ))}
        </Swiper>
      </div>
      {vehicles?.length > 1 && <div className={cx(styles.bullets, bulletClassName)}></div>}
    </section>
  ) : (
    <></>
  );
};

export default VehicleContainer;
